import type * as React from "react";
import { cn } from "@/lib/utils";

// ─── Root ────────────────────────────────────────────────────────────────────

export type ErrorTemplateProps = React.ComponentProps<"div">;

export function ErrorTemplate({ className, ...props }: ErrorTemplateProps) {
  return (
    <div
      className={cn(
        "relative flex min-h-svh w-full flex-col items-center justify-center overflow-hidden bg-background pb-12 text-foreground",
        className
      )}
      {...props}
    />
  );
}

// ─── Logo ────────────────────────────────────────────────────────────────────

export type ErrorTemplateLogoProps = React.ComponentProps<"div">;

export function ErrorTemplateLogo({
  className,
  ...props
}: ErrorTemplateLogoProps) {
  return <div className={cn("mb-6", className)} {...props} />;
}

// ─── Icon ────────────────────────────────────────────────────────────────────

export type ErrorTemplateIconProps = React.ComponentProps<"div">;

export function ErrorTemplateIcon({
  className,
  ...props
}: ErrorTemplateIconProps) {
  return <div className={cn("mb-6", className)} {...props} />;
}

// ─── Content ─────────────────────────────────────────────────────────────────

export type ErrorTemplateContentProps = React.ComponentProps<"main">;

export function ErrorTemplateContent({
  className,
  ...props
}: ErrorTemplateContentProps) {
  return (
    <main
      className={cn(
        "relative mx-auto flex w-full max-w-2xl flex-col items-center text-center",
        className
      )}
      {...props}
    />
  );
}

// ─── Title ───────────────────────────────────────────────────────────────────

export type ErrorTemplateTitleProps = React.ComponentProps<"h1">;

export function ErrorTemplateTitle({
  className,
  ...props
}: ErrorTemplateTitleProps) {
  return (
    <h1
      className={cn(
        "font-medium text-muted-foreground text-sm uppercase tracking-[0.2em]",
        className
      )}
      {...props}
    />
  );
}

// ─── Description ─────────────────────────────────────────────────────────────

export type ErrorTemplateDescriptionProps = React.ComponentProps<"p">;

export function ErrorTemplateDescription({
  className,
  ...props
}: ErrorTemplateDescriptionProps) {
  return (
    <p
      className={cn(
        "mx-auto mt-3 max-w-md text-muted-foreground text-sm leading-relaxed",
        className
      )}
      {...props}
    />
  );
}

// ─── URL Pill ────────────────────────────────────────────────────────────────

export type ErrorTemplateUrlProps = {
  url: string;
} & Omit<React.ComponentProps<"div">, "children">;

export function ErrorTemplateUrl({
  className,
  url,
  ...props
}: ErrorTemplateUrlProps) {
  return (
    <div
      className={cn(
        "mt-5 inline-flex max-w-sm items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-1.5",
        className
      )}
      {...props}
    >
      <span className="shrink-0 text-muted-foreground text-xs uppercase tracking-widest">
        URL
      </span>
      <span className="h-3 w-px bg-border/60" />
      <span className="truncate font-mono text-foreground/70 text-xs">
        {url}
      </span>
    </div>
  );
}

// ─── Actions ─────────────────────────────────────────────────────────────────

export type ErrorTemplateActionsProps = React.ComponentProps<"div">;

export function ErrorTemplateActions({
  className,
  ...props
}: ErrorTemplateActionsProps) {
  return (
    <div
      className={cn(
        "mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center",
        className
      )}
      {...props}
    />
  );
}
